import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Settings2, Grid3X3, Type } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

export interface PDFSettingsState {
  pageSize: string;
  orientation: 'portrait' | 'landscape';
  margin: number;
  compression: number;
  layout: 'single' | 'grid-2x2';
  includeOCR: boolean;
}

interface PDFSettingsProps {
  settings: PDFSettingsState;
  onChange: (s: PDFSettingsState) => void;
}

const PDFSettings = ({ settings, onChange }: PDFSettingsProps) => {
  const update = (key: keyof PDFSettingsState, value: any) => {
      onChange({ ...settings, [key]: value });
  };

  return (
    <Card className="h-full border-gray-100 dark:border-white/5">
        <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-2 pb-4 border-b border-gray-100 dark:border-white/5">
                <Settings2 className="w-5 h-5 text-gray-500" />
                <h3 className="font-semibold">PDF Configuration</h3>
            </div>

            <div className="space-y-5">
                {/* Page Size */}
                <div className="space-y-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label className="text-xs font-bold uppercase text-muted-foreground cursor-help">Page Size</Label>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-[10px]">Set the paper size for the generated PDF document.</p></TooltipContent>
                    </Tooltip>
                    <Select value={settings.pageSize} onValueChange={(v) => update('pageSize', v)}>
                        <SelectTrigger className="bg-gray-100 dark:bg-white/5 border-transparent focus:ring-0 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-950 border-gray-100 dark:border-white/10 shadow-lg">
                            <SelectItem value="a4">A4 (Standard)</SelectItem>
                            <SelectItem value="letter">US Letter</SelectItem>
                            <SelectItem value="legal">Legal</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Orientation */}
                <div className="space-y-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Label className="text-xs font-bold uppercase text-muted-foreground cursor-help">Orientation</Label>
                      </TooltipTrigger>
                      <TooltipContent><p className="text-[10px]">Portrait: taller than wide. Landscape: wider than tall.</p></TooltipContent>
                    </Tooltip>
                    <div className="flex bg-gray-100 dark:bg-white/5 p-1 rounded-lg">
                        <button 
                            onClick={() => update('orientation', 'portrait')}
                            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${settings.orientation === 'portrait' ? 'bg-white dark:bg-indigo-600 shadow-sm text-black dark:text-white' : 'text-muted-foreground'}`}
                        >
                            Portrait
                        </button>
                        <button 
                            onClick={() => update('orientation', 'landscape')}
                            className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-all ${settings.orientation === 'landscape' ? 'bg-white dark:bg-indigo-600 shadow-sm text-black dark:text-white' : 'text-muted-foreground'}`}
                        >
                            Landscape
                        </button>
                    </div>
                </div>

                {/* Layout Mode */}
                <div className="space-y-2">
                     <Tooltip>
                       <TooltipTrigger asChild>
                         <Label className="text-xs font-bold uppercase text-muted-foreground flex items-center gap-2 cursor-help">
                            <Grid3X3 className="w-3 h-3" /> Layout
                         </Label>
                       </TooltipTrigger>
                       <TooltipContent><p className="text-[10px]">Single: one image per page. Grid: fit 4 images on each page in a 2x2 collage.</p></TooltipContent>
                     </Tooltip>
                     <Select value={settings.layout} onValueChange={(v) => update('layout', v)}>
                        <SelectTrigger className="bg-gray-100 dark:bg-white/5 border-transparent focus:ring-0 text-sm">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-zinc-950 border-gray-100 dark:border-white/10 shadow-lg">
                            <SelectItem value="single">1 Image per Page</SelectItem>
                            <SelectItem value="grid-2x2">2x2 Grid (Collage)</SelectItem>
                        </SelectContent>
                    </Select>
                </div>

                {/* Compression */}
                <div className="space-y-2">
                    <div className="flex justify-between">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Label className="text-xs font-bold uppercase text-muted-foreground cursor-help">Quality</Label>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-[10px]">Image compression level. Lower = smaller file size but more quality loss.</p></TooltipContent>
                        </Tooltip>
                        <span className="text-xs font-mono">{Math.round(settings.compression * 100)}%</span>
                    </div>
                    <Slider 
                        value={[settings.compression]} 
                        onValueChange={(v) => update('compression', v[0])}
                        min={0.1} max={1} step={0.1} 
                    />
                </div>

                {/* OCR Toggle */}
                <div className="pt-4 border-t border-gray-100 dark:border-white/5">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                           <Type className="w-4 h-4 text-indigo-500" />
                           <Tooltip>
                             <TooltipTrigger asChild>
                               <Label className="text-sm font-medium cursor-help">Text Recognition (OCR)</Label>
                             </TooltipTrigger>
                             <TooltipContent><p className="text-[10px]">Extract text from images using optical character recognition, making the PDF searchable.</p></TooltipContent>
                           </Tooltip>
                        </div>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Switch 
                                checked={settings.includeOCR}
                                onCheckedChange={(c) => update('includeOCR', c)}
                            />
                          </TooltipTrigger>
                          <TooltipContent><p className="text-[10px]">{settings.includeOCR ? 'OCR is enabled — text will be extracted from images' : 'Enable OCR to extract text from images for searchability'}</p></TooltipContent>
                        </Tooltip>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1 ml-6">
                        Automatically detects text and appends it to the PDF for searchability. May increase processing time.
                    </p>
                </div>
            </div>
        </CardContent>
    </Card>
  );
};

export default PDFSettings;
